import { useNavigate } from "react-router-dom";
import { UserNotificationsEnum } from "../../../../../app/pages/auth/userNotifications.enum";
import { SessionService } from "../../../../../app/base/services/session.service";
import { LocalStorageService } from "../../../../../app/base/services/local-storage.service";
import { httpService } from "../../../../../app/base/services/httpService.service";

const N = UserNotificationsEnum;

const MY_WORKSPACES = "/Workspaces/get-my-workspaces?pageSize=100&pageNumber=1";
// The expert isn't a workspace member; it sees every workspace (AllWorkspace).
const ALL_WORKSPACES = "/Workspaces?pageSize=100&pageNumber=1";

// Module status ids used by /Surveys/manager-survey and /Surveys/staff-survey.
const MANAGER_TAB_BY_STATUS = { 4: "pending", 3: "approved", 5: "rejected" };
const STAFF_STATUSES = [1, 4, 5, 3];

const getList = (url) =>
   new Promise((resolve) => {
      httpService
         .get(
            url,
            (res) => {
               const data = res?.data?.data ?? res?.data;
               resolve(Array.isArray(data) ? data : []);
            },
            () => resolve([])
         )
         .catch(() => resolve([]));
   });

/**
 * Most notifications come without a workspaceId, and the pages they lead to only work
 * inside a workspace. Returns the workspace ids to search: the ones the notification
 * text names ("... Inside workspaces: Country Offices"; names aren't translated, and the
 * longest match goes first so "External Partners - Qualified" beats "External
 * Partners"), or else the current workspace followed by the rest. `named` is the best
 * named one, the fallback when the item itself can't be found (its round ended, it was
 * removed).
 */
const getWorkspaces = async (workspacesUrl, text) => {
   const workspaces = await getList(workspacesUrl);
   const ids = workspaces.map((w) => w.id);
   const named = text
      ? workspaces
           .filter((w) => w?.name && text.includes(w.name.trim()))
           .sort((a, b) => b.name.trim().length - a.name.trim().length)
           .map((w) => w.id)
      : [];
   // A named workspace is where the item is; searching the others too only adds requests.
   const current = SessionService.getItem("workspaceID");
   const ordered = named.length
      ? named
      : [...new Set([...(ids.includes(current) ? [current] : []), ...ids])];
   return { ordered, named: named[0] || null };
};

/**
 * Searches one workspace at a time, in order, and stops at the first hit, so a click
 * usually costs a handful of requests (searching everything at once got rate-limited).
 * `listUrls(workspaceId)` gives the lists to check in that workspace, in preference
 * order. Resolves { workspaceId, rows, index } (index = which list matched) or null.
 */
const findInWorkspaces = async (ordered, listUrls, matches) => {
   for (const workspaceId of ordered) {
      const lists = await Promise.all(listUrls(workspaceId).map(getList));
      for (let index = 0; index < lists.length; index++) {
         const rows = lists[index].filter(matches);
         if (rows.length) return { workspaceId, rows, index };
      }
   }
   return null;
};

const isModule = (moduleId) => (row) => row?.survey?.id === moduleId || row?.id === moduleId;

export const useNotificationRedirect = () => {
   const navigate = useNavigate();

   // Only overwrite the workspace with a real id: SessionService stores null as "null",
   // which drops the user out of the workspace they're in.
   const enterWorkspace = (workspaceId) => {
      if (workspaceId) SessionService.setItem("workspaceID", workspaceId);
   };

   // Nothing to point at (or it no longer exists): the user's workspaces.
   const toWorkspaces = () => navigate("/workspace");

   const organizationId = () => LocalStorageService.getItem("user")?.organizationId;

   const toGuidedAssessment = async (assessmentId, body) => {
      const { ordered } = await getWorkspaces(MY_WORKSPACES, body);
      const found =
         assessmentId &&
         (await findInWorkspaces(
            ordered,
            (wid) => [`/Workspaces/${wid}/guided-assessments?organizationId=${organizationId()}`],
            (row) => row?.id === assessmentId
         ));
      if (!found) return toWorkspaces();
      enterWorkspace(found.workspaceId);
      navigate(`/workspace/assessment/guided-assessment/${assessmentId}`);
   };

   const toManagerAssessment = async (assessmentId, body) => {
      const { ordered, named } = await getWorkspaces(MY_WORKSPACES, body);
      const found =
         assessmentId &&
         (await findInWorkspaces(
            ordered,
            (wid) => [`/Assesments/assign-manager?WorkspaceId=${wid}`],
            (row) => row?.id === assessmentId
         ));
      if (found) {
         enterWorkspace(found.workspaceId);
         return navigate(`/workspace/assessment/self-assessment/${assessmentId}`);
      }
      if (!named) return toWorkspaces();
      enterWorkspace(named);
      navigate("/workspace/assessment/self-assessment/");
   };

   const toManagerModules = async (moduleId, workspaceId, statuses, body) => {
      const { ordered, named } = await getWorkspaces(MY_WORKSPACES, body);
      const found =
         moduleId &&
         (await findInWorkspaces(
            workspaceId ? [workspaceId, ...ordered.filter((id) => id !== workspaceId)] : ordered,
            (wid) => statuses.map((s) => `/Surveys/manager-survey?StatusId=${s}&WorkspaceId=${wid}`),
            isModule(moduleId)
         ));
      const target = found?.workspaceId || workspaceId || named;
      if (!target) return toWorkspaces();
      enterWorkspace(target);
      // ViewActionPlanManager opens on this tab: where the module is now, which may have
      // moved on since the notification.
      LocalStorageService.setItem("managerReturnTab", MANAGER_TAB_BY_STATUS[statuses[found?.index ?? 0]]);
      // Trailing slash: Aside matches "workspace/survey-request/" for this page's layout.
      navigate("/workspace/survey-request/");
   };

   const toStaffModules = async (moduleId, body) => {
      const { ordered, named } = await getWorkspaces(MY_WORKSPACES, body);
      const found =
         moduleId &&
         (await findInWorkspaces(
            ordered,
            (wid) => STAFF_STATUSES.map((s) => `/Surveys/staff-survey?StatusId=${s}&WorkspaceId=${wid}`),
            isModule(moduleId)
         ));
      const target = found?.workspaceId || named;
      if (!target) return toWorkspaces();
      enterWorkspace(target);
      navigate("/workspace/task-management?tab=modules");
   };

   const toExpertAssessment = async (assessmentId, body) => {
      const { ordered, named } = await getWorkspaces(ALL_WORKSPACES, body);
      const found =
         assessmentId &&
         (await findInWorkspaces(
            ordered,
            (wid) => [`/Workspaces/${wid}/consultant/organizations`],
            (row) => row?.assessment?.id === assessmentId
         ));
      const target = found?.workspaceId || named;
      if (!target) return toWorkspaces();
      enterWorkspace(target);
      // The same assessment can be assigned for several organizations; the notification
      // doesn't say which, so then the expert picks from the list.
      if (found?.rows.length !== 1) return navigate("/workspace/assessment/guided-assessment/list");
      // Same state the list sets when a row is opened (GuidedAssessmentTable).
      SessionService.setItem("consultOrg", found.rows[0].organizationId);
      LocalStorageService.setItem("selectedRecord", assessmentId);
      navigate(`/workspace/assessment/guided-assessment/surveys/${assessmentId}`);
   };

   const notificationRedirect = (code, entityId, workspaceID, body) => {
      switch (code) {
         // Admin
         case N.AdminRegistration:
            return navigate(`/admin/request`);
         case N.AdminWorkspaceRequest:
            return navigate(`/admin/request?tab=2`);
         case N.AdminAssessmentRequest:
            return navigate(`/admin/request?tab=3`);
         case N.AdminExpertRequest:
            return navigate(`/admin/teams`);
         case N.AdminAddedTicket:
         case N.AdminAddedTicketMessage:
            return navigate(`/admin/help-desk/details/${entityId}`);

         // Organization
         case N.NgoApprovedWorkspace:
            SessionService.setItem("workspaceType", "1");
            return navigate(`/workspace`);
         case N.NgoRejectedWorkspace:
            SessionService.setItem("workspaceType", "2");
            return navigate(`/workspace`);
         case N.NgoApprovedAssessment:
         case N.NgoRejectedAssessment:
         case N.NgoCompletedAssessment:
            return toGuidedAssessment(entityId, body);
         case N.NgoAddedTicketMessage:
            enterWorkspace(workspaceID);
            return navigate(`/workspace/help-desk/details/${entityId}`);

         // Manager. The module list is per status tab; deep-linking into the module
         // preview needs a draftId the payload doesn't carry.
         case N.ManagerAddedAssignee:
            return toManagerAssessment(entityId, body);
         case N.ManagerRequestedSubmit:
            return toManagerModules(entityId, workspaceID, [4, 3, 5], body);
         case N.ManagerApprovedSubmitRequest:
            return toManagerModules(entityId, workspaceID, [3, 4, 5], body);
         case N.ManagerRejectedSubmitRequest:
            return toManagerModules(entityId, workspaceID, [5, 4, 3], body);

         // Staff
         case N.StaffAddedAssignee:
         case N.StaffApprovedSubmitRequest:
         case N.StaffRejectedSubmitRequest:
            return toStaffModules(entityId, body);

         // Expert
         case N.ExpertAssessmentRequest:
            return toExpertAssessment(entityId, body);

         // Approver: no workspace context. The review page needs state carried from the
         // list row, so land on the list.
         case N.ApproverRequestedApproval:
            return navigate(`/approver/submissions`);

         // Removals have nothing left to open.
         case N.ManagerRemovedAssignee:
         case N.StaffRemovedAssignee:
            return toWorkspaces();

         default:
            return;
      }
   };

   return notificationRedirect;
};
